/**
 * A3 (2026-06-27) — Customer Login (web).
 *
 * Single-identifier field "Email or Phone Number" + Password + Google +
 * Apple + "Create an account" link. Posts `{identifier, password}` to
 * /api/auth/login and runs the gated router on success (change-password
 * → complete-profile → customer home).
 *
 * No Forgot Password. No OTP. Social buttons are hidden entirely when
 * their env is missing (not greyed stubs).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/lib/auth';
import { IS_CUSTOMER_HOST } from '@/lib/hostnameGuard';
import { Loader2, AlertCircle } from 'lucide-react';
import { SocialAuthButtons } from '@/components/customer/SocialAuthButtons';

export default function CustomerLoginEmailPage() {
  const signInWithIdentifier = useAuthStore((s) => s.signInWithIdentifier);
  const signInWithToken = useAuthStore((s) => s.signInWithToken);
  const signOut = useAuthStore((s) => s.signOut);

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    try {
      const u = await signInWithIdentifier(identifier, password);
      if (u?.role && u.role !== 'customer') {
        // The customer login screen is customer-only. Staff accounts
        // must use /login (host-aware). Clear the token so we don't
        // leak staff privileges into this surface.
        signOut();
        setErr({
          title: 'Customer accounts only',
          message: IS_CUSTOMER_HOST
            ? "This domain is for customers only. If you're a staff member, please sign in at the staff portal."
            : 'This sign-in is for customers only.',
        });
      }
      // Successful customer path: the gated router inside
      // `signInWithIdentifier` already navigated — nothing to do here.
    } catch (e2) {
      setErr({
        title: 'Sign-in failed',
        message:
          e2?.detail ?? 'Check your email/phone and password and try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const onSocialError = (msg) => {
    setErr({ title: 'Social sign-in failed', message: msg });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-10 justify-center">
          <img
            src="/onestamp-logo.png"
            alt="OneStamp"
            className="w-12 h-12 rounded-2xl"
          />
          <div className="text-xs text-gold uppercase tracking-widest font-semibold">
            OneStamp
          </div>
        </div>

        <div className="rounded-3xl bg-[#15151B] border border-white/8 p-7 shadow-2xl">
          <h1 className="font-display text-2xl font-semibold mb-2">
            Welcome back
          </h1>
          <p className="text-sm text-white/55 mb-6">
            Sign in to keep collecting stamps across every OneStamp brand.
          </p>

          {err ? (
            <div
              className="mb-5 px-3 py-2.5 rounded-xl bg-rose-500/8 border border-rose-400/25 flex items-start gap-2 text-xs"
              data-testid="customer-login-error"
              role="alert"
            >
              <AlertCircle size={14} className="text-rose-300 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium text-rose-200">{err.title}</div>
                <div className="text-rose-200/80 mt-0.5">{err.message}</div>
              </div>
            </div>
          ) : null}

          <form
            onSubmit={onSubmit}
            className="space-y-4"
            data-testid="customer-login-form"
            noValidate
          >
            <div>
              <label className="block text-xs font-mono text-white/55 mb-1.5 uppercase tracking-wider">
                Email or phone number
              </label>
              <input
                type="text"
                autoComplete="username"
                required
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                data-testid="customer-login-identifier"
                className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-gold/40 focus:bg-black/40 transition-colors"
                placeholder="you@example.com or +62 812 …"
              />
              {/* Hidden mirror so legacy e2e selectors keep working */}
              <input
                type="hidden"
                value={identifier}
                data-testid="customer-login-email-input"
                readOnly
              />
            </div>
            <div>
              <label className="block text-xs font-mono text-white/55 mb-1.5 uppercase tracking-wider">
                Password
              </label>
              <input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="customer-login-password"
                className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-gold/40 focus:bg-black/40 transition-colors"
                placeholder="••••••••"
              />
            </div>

            <label className="flex items-center gap-2 text-xs text-white/55 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                data-testid="customer-login-remember"
                className="w-4 h-4 accent-gold"
              />
              Keep me signed in on this device
            </label>

            <button
              type="submit"
              disabled={submitting}
              data-testid="customer-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-semibold rounded-xl py-2.5 text-sm hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-glow-gold-sm"
            >
              {submitting ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Signing in…
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          <SocialAuthButtons
            busy={submitting}
            onSuccess={async (res) => {
              setErr(null);
              await signInWithToken(res);
            }}
            onError={onSocialError}
          />

          <div className="mt-6 text-center text-xs text-white/55">
            New here?{' '}
            <Link
              to="/customer/signup"
              data-testid="customer-login-signup-link"
              className="text-gold font-semibold hover:text-gold-light"
            >
              Create an account
            </Link>
          </div>

          <div className="mt-8 text-center text-[11px] text-white/35">
            <Link
              to="/privacy"
              data-testid="customer-login-privacy-link"
              className="hover:text-white/70 transition-colors"
            >
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
